/**
 * Inline flow diagrams for selected SEO pages (used by SeoSectionBlocks + SeoDiagram).
 * @typedef {{ insertAfterBlockIndex: number, microIntro?: string, title?: string, description?: string, figcaption: string, ariaLabel: string, steps: { label: string, description?: string }[] }} SeoDiagramSpec
 */

/** @type {Record<string, SeoDiagramSpec>} */
export const SEO_PAGE_DIAGRAMS = {
  '/security': {
    insertAfterBlockIndex: 1,
    microIntro: "Here's how secure document signing works step by step:",
    title: 'Secure signing flow',
    description: 'From draft document to stored audit context—each step uses HTTPS and account-scoped access.',
    figcaption: 'Secure e-signature process with signer verification and a stored audit trail.',
    ariaLabel:
      'Flow diagram: document, add fields, send, signer verification, signature applied, audit trail stored',
    steps: [
      { label: 'Document', description: 'Agreement or PDF in your workspace' },
      { label: 'Add fields', description: 'Signatures, dates, and inputs' },
      { label: 'Send', description: 'Unique link to each signer' },
      { label: 'Signer verifies', description: 'Browser session and checks you enable' },
      { label: 'Signature applied', description: 'Captured to the agreement record' },
      { label: 'Audit trail stored', description: 'Completion metadata for the owner' },
    ],
  },
  '/esignature-legality': {
    insertAfterBlockIndex: 0,
    microIntro: 'This is how an e-signature becomes legally binding:',
    title: 'E-signature legal validation flow',
    description: 'How routine e-signatures build a defensible record—not legal advice; requirements vary by jurisdiction.',
    figcaption: 'How e-signatures become legally binding through identity, intent, and audit trail.',
    ariaLabel:
      'Flow diagram: user identity, intent to sign, signature action, timestamp, audit trail, legally binding record',
    steps: [
      { label: 'User identity', description: 'Account and session context' },
      { label: 'Intent to sign', description: 'Clear actions on the agreement' },
      { label: 'Signature action', description: 'Fields completed in sequence' },
      { label: 'Timestamp', description: 'When the event occurred' },
      { label: 'Audit trail', description: 'Evidence-friendly metadata' },
      { label: 'Binding record', description: 'Signed PDF + completion log' },
    ],
  },
}

/**
 * @param {string} pathname — logical path e.g. `/security`
 * @returns {SeoDiagramSpec | null}
 */
export function getSeoPageDiagram(pathname) {
  if (!pathname || typeof pathname !== 'string') return null
  const p = pathname.trim()
  return SEO_PAGE_DIAGRAMS[p] ?? null
}
